import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Heading,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  toast,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  keyframes
} from '@sparrowengg/twigs-react';
import {
  SearchIcon,
  CopyIcon,
  CalendarIcon,
  ChevronDownIcon
} from '@sparrowengg/twigs-react-icons';
import { devPortalServices } from 'commons/services';
import { EventTrace, EventLogsFilters, EventTypeOption } from '../types';
import ExecutionDetailsModal from './execution-details-modal';

type SearchMode = 'query' | 'traceId';

const shimmerSweep = keyframes({
  '0%': { backgroundPosition: '200% 0' },
  '100%': { backgroundPosition: '-200% 0' }
});

const ShimmerBlock: React.FC<{
  width?: number | string;
  height?: number;
  radius?: string;
  delay?: number;
}> = ({ width = '100%', height = 14, radius = '6px', delay = 0 }) => (
  <Box
    css={{
      width,
      height,
      borderRadius: radius,
      backgroundImage:
        'linear-gradient(90deg, #ECECEC 25%, #F8F8F8 50%, #ECECEC 75%)',
      backgroundSize: '400% 100%',
      animation: `${shimmerSweep} 1.6s ease-in-out infinite`,
      animationDelay: `${delay}s`
    }}
  />
);

const EventLogsTableSkeleton: React.FC<{ columnWidths: ColumnWidths }> = ({
  columnWidths
}) => (
  <Box
    css={{
      borderRadius: '$lg',
      border: '1px solid #E8E8E8',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    }}
  >
    <Flex
      css={{ backgroundColor: '#F5F5F5', borderBottom: '1px solid #E8E8E8' }}
    >
      {[
        { label: 'Trace ID', width: columnWidths.traceId, align: 'flex-start' },
        {
          label: 'Event Type',
          width: columnWidths.eventType,
          align: 'flex-start'
        },
        { label: 'Total Apps', width: columnWidths.totalApps, align: 'center' },
        { label: 'Success', width: columnWidths.success, align: 'center' },
        { label: 'Failed', width: columnWidths.failed, align: 'center' },
        {
          label: 'Started At',
          width: columnWidths.startedAt,
          align: 'flex-start'
        },
        { label: 'Status', width: columnWidths.status, align: 'center' }
      ].map((col) => (
        <Flex
          key={col.label}
          justifyContent={col.align as any}
          css={{ width: col.width, padding: '$12', flexShrink: 0 }}
        >
          <Text weight="bold" size="sm" css={{ color: '#333333' }}>
            {col.label}
          </Text>
        </Flex>
      ))}
    </Flex>

    {Array.from({ length: 8 }).map((_, rowIndex) => (
      <Flex
        key={rowIndex}
        alignItems="center"
        css={{
          borderBottom: '1px solid #E8E8E8',
          opacity: 1 - rowIndex * 0.06
        }}
      >
        <Flex
          alignItems="center"
          gap="$6"
          css={{ width: columnWidths.traceId, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock
            width={22}
            height={22}
            radius="6px"
            delay={rowIndex * 0.06}
          />
          <ShimmerBlock width={110} height={14} delay={rowIndex * 0.06} />
        </Flex>
        <Box
          css={{ width: columnWidths.eventType, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock width={90} height={14} delay={rowIndex * 0.06} />
        </Box>
        <Flex
          justifyContent="center"
          css={{ width: columnWidths.totalApps, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock width={24} height={14} delay={rowIndex * 0.06} />
        </Flex>
        <Flex
          justifyContent="center"
          css={{ width: columnWidths.success, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock
            width={36}
            height={22}
            radius="6px"
            delay={rowIndex * 0.06}
          />
        </Flex>
        <Flex
          justifyContent="center"
          css={{ width: columnWidths.failed, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock
            width={36}
            height={22}
            radius="6px"
            delay={rowIndex * 0.06}
          />
        </Flex>
        <Box
          css={{ width: columnWidths.startedAt, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock width={150} height={14} delay={rowIndex * 0.06} />
        </Box>
        <Flex
          justifyContent="center"
          css={{ width: columnWidths.status, padding: '$12', flexShrink: 0 }}
        >
          <ShimmerBlock
            width={72}
            height={22}
            radius="6px"
            delay={rowIndex * 0.06}
          />
        </Flex>
      </Flex>
    ))}
  </Box>
);

const SearchModeSwitch: React.FC<{
  mode: SearchMode;
  onChange: (mode: SearchMode) => void;
}> = ({ mode, onChange }) => (
  <Box
    css={{
      position: 'relative',
      display: 'flex',
      width: 200,
      backgroundColor: '#F0F0F0',
      borderRadius: '$3xl',
      padding: 4,
      marginBottom: '$16',
      userSelect: 'none'
    }}
  >
    <Box
      css={{
        position: 'absolute',
        top: 4,
        bottom: 4,
        left: mode === 'query' ? 4 : '50%',
        width: 'calc(50% - 4px)',
        backgroundColor: '#4A9CA6',
        borderRadius: '$3xl',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
        transition: 'left 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'left'
      }}
    />
    <Box
      onClick={() => onChange('query')}
      css={{
        position: 'relative',
        zIndex: 1,
        flex: 1,
        padding: '$6 0',
        borderRadius: '$3xl',
        cursor: 'pointer',
        textAlign: 'center',
        fontSize: '$xs',
        fontWeight: 500,
        color: mode === 'query' ? 'white' : '#666666',
        transition: 'color 0.28s ease'
      }}
    >
      Query Search
    </Box>
    <Box
      onClick={() => onChange('traceId')}
      css={{
        position: 'relative',
        zIndex: 1,
        flex: 1,
        padding: '$6 0',
        borderRadius: '$3xl',
        cursor: 'pointer',
        textAlign: 'center',
        fontSize: '$xs',
        fontWeight: 500,
        color: mode === 'traceId' ? 'white' : '#666666',
        transition: 'color 0.28s ease'
      }}
    >
      Trace ID Search
    </Box>
  </Box>
);

const StatusBadge: React.FC<{
  status: string;
  variant?: 'success' | 'error' | 'default';
}> = ({ status, variant = 'default' }) => {
  const colors = {
    success: { bg: '#E6F4F1', text: '#0F6B59', border: '#B3E5D9' },
    error: { bg: '#FEE5E5', text: '#B42318', border: '#F4CCCC' },
    default: { bg: '#F5F5F5', text: '#666666', border: '#E0E0E0' }
  };
  const color = colors[variant];
  return (
    <Box
      css={{
        display: 'inline-block',
        backgroundColor: color.bg,
        color: color.text,
        padding: '$4 $8',
        borderRadius: '$sm',
        border: `1px solid ${color.border}`,
        fontSize: '$sm',
        fontWeight: 500,
        whiteSpace: 'nowrap'
      }}
    >
      {status}
    </Box>
  );
};

const ResizeHandle: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  isActive?: boolean;
}> = ({ onMouseDown, isActive }) => (
  <Box
    onMouseDown={onMouseDown}
    css={{
      position: 'absolute',
      right: -4,
      top: 0,
      width: 9,
      height: '100%',
      cursor: 'col-resize',
      userSelect: 'none',
      display: 'flex',
      justifyContent: 'center',
      zIndex: 1,
      '&:hover > div': {
        backgroundColor: '#4A9CA6',
        width: 2
      }
    }}
  >
    <Box
      css={{
        width: 1,
        height: '60%',
        alignSelf: 'center',
        backgroundColor: isActive ? '#4A9CA6' : '#D0D0D0',
        borderRadius: '$full',
        transition: 'background-color 0.15s ease, width 0.15s ease'
      }}
    />
  </Box>
);

const TruncatedTraceId: React.FC<{ traceId: string; maxWidth: number }> = ({
  traceId,
  maxWidth
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(traceId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Copied',
        description: 'Trace ID copied to clipboard',
        variant: 'default'
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Reserve space for the copy icon + gap so the text column shrinks/grows with the resized column
  const textMaxWidth = Math.max(40, maxWidth - 40);

  return (
    <Flex alignItems="center" gap="$6" css={{ minWidth: 0 }}>
      <Text
        size="sm"
        css={{
          color: '#999999',
          fontFamily: 'monospace',
          maxWidth: textMaxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={traceId}
      >
        {traceId}
      </Text>
      <IconButton
        icon={<CopyIcon />}
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy trace ID'}
        css={{
          flexShrink: 0,
          color: copied ? '#4A9CA6' : '#999999',
          cursor: 'pointer',
          '&:hover': {
            color: '#4A9CA6'
          }
        }}
      />
    </Flex>
  );
};

const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const DEFAULT_PAGE_SIZE = 50;

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

interface ColumnWidths {
  traceId: number;
  eventType: number;
  totalApps: number;
  success: number;
  failed: number;
  startedAt: number;
  status: number;
}

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  traceId: 200,
  eventType: 150,
  totalApps: 100,
  success: 100,
  failed: 100,
  startedAt: 180,
  status: 120
};

const EventLogsList: React.FC = () => {
  const [eventTraces, setEventTraces] = useState<EventTrace[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrace, setSelectedTrace] = useState<EventTrace | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const [filters, setFilters] = useState<EventLogsFilters>({
    pageNumber: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [appIdInput, setAppIdInput] = useState('');
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(
    DEFAULT_COLUMN_WIDTHS
  );
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);

  const [searchMode, setSearchMode] = useState<SearchMode>('query');
  const [traceIdInput, setTraceIdInput] = useState('');
  const [eventTypeOptions, setEventTypeOptions] = useState<EventTypeOption[]>(
    []
  );
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  const currentPage = (filters.pageNumber ?? 0) + 1;
  const totalPages = Math.ceil(
    totalCount / (filters.pageSize ?? DEFAULT_PAGE_SIZE)
  );

  const fetchEventLogs = async () => {
    try {
      setLoading(true);
      const response = await devPortalServices.getEventLogs(filters);
      if (response?.eventTraces) {
        setEventTraces(response.eventTraces);
        setTotalCount(response.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching event logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch event logs',
        variant: 'error'
      });
      setEventTraces([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchMode === 'traceId' && !filters.traceId) {
      setEventTraces([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    fetchEventLogs();
  }, [filters, searchMode]);

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const response = await devPortalServices.getAllProductEvents();
        const categories = response?.data || [];
        const allEvents = categories.flatMap(
          (category: any) => category.events || []
        );
        const uniqueByName = new Map<string, EventTypeOption>();
        allEvents.forEach((event: any) => {
          if (event?.name && !uniqueByName.has(event.name)) {
            uniqueByName.set(event.name, { id: event.id, name: event.name });
          }
        });
        setEventTypeOptions(Array.from(uniqueByName.values()));
      } catch (error) {
        console.error('Error fetching event types:', error);
      }
    };
    fetchEventTypes();
  }, []);

  const handleMouseDown = (column: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setResizingColumn(column);
  };

  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.movementX;
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: Math.max(
          80,
          prev[resizingColumn as keyof ColumnWidths] + delta
        )
      }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn]);

  const formatToISO = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString();
  };

  const handleApplyFilters = () => {
    const newFilters: EventLogsFilters = {
      pageNumber: 0,
      pageSize: filters.pageSize || DEFAULT_PAGE_SIZE
    };

    if (dateRange.from) {
      newFilters.startDate = dateRange.from;
    }

    if (dateRange.to) {
      newFilters.endDate = dateRange.to;
    }

    if (appIdInput.trim()) {
      newFilters.appId = appIdInput.trim();
    }

    if (selectedEventId) {
      newFilters.eventId = selectedEventId;
    }

    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setDateRange({ from: '', to: '' });
    setAppIdInput('');
    setSelectedEventId('');
    setFilters({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE
    });
  };

  const handleTraceIdSearch = () => {
    if (!traceIdInput.trim()) return;
    setFilters({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      traceId: traceIdInput.trim()
    });
  };

  const handleClearTraceIdSearch = () => {
    setTraceIdInput('');
    setFilters({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE
    });
  };

  const handleSearchModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setDateRange({ from: '', to: '' });
    setAppIdInput('');
    setSelectedEventId('');
    setTraceIdInput('');
    setFilters({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({
      ...filters,
      pageNumber: newPage
    });
  };

  const handlePageSizeChange = (size: string) => {
    setFilters({
      ...filters,
      pageSize: parseInt(size),
      pageNumber: 0
    });
  };

  const getStatusColor = (isFailed: boolean, isCompleted: boolean) => {
    if (!isCompleted) return '$warning600';
    if (isFailed) return '$negative600';
    return '$positive600';
  };

  const getStatusLabel = (isFailed: boolean, isCompleted: boolean) => {
    if (!isCompleted) return 'In Progress';
    if (isFailed) return 'Failed';
    return 'Completed';
  };

  const hasActiveFilters =
    dateRange.from || dateRange.to || appIdInput || selectedEventId;

  return (
    <Box
      css={{
        padding: '$20 $40',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto'
      }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        css={{ marginBottom: '$24' }}
      >
        <Heading size="h5">Event Logs</Heading>
        <Text size="sm" css={{ color: '$neutral600' }}>
          Total Events: {totalCount}
        </Text>
      </Flex>

      {/* Search Mode Switch */}
      <SearchModeSwitch mode={searchMode} onChange={handleSearchModeChange} />

      {searchMode === 'query' ? (
        /* Filters Section - Simple Compact with Calendar */
        <Box
          css={{
            backgroundColor: '#FAFAFA',
            borderRadius: '$lg',
            padding: '$12',
            marginBottom: '$20',
            border: '1px solid #E8E8E8'
          }}
        >
          <Flex gap="$12" alignItems="flex-end" css={{ flexWrap: 'wrap' }}>
            <Box css={{ flex: 1, minWidth: 220 }}>
              <Text
                weight="medium"
                size="sm"
                css={{ marginBottom: '$4', color: '#333333' }}
              >
                From
              </Text>
              <Box css={{ position: 'relative' }}>
                <Input
                  type="text"
                  placeholder="2026-07-08T00:00:00.000Z"
                  value={dateRange.from}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, from: e.currentTarget.value })
                  }
                  css={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '$sm',
                    padding: '$8 $36 $8 $8',
                    backgroundColor: '#FFFFFF',
                    paddingRight: '$36'
                  }}
                />
                <Input
                  type="date"
                  onChange={(e) => {
                    if (e.currentTarget.value) {
                      const date = new Date(
                        e.currentTarget.value + 'T00:00:00.000Z'
                      );
                      setDateRange({ ...dateRange, from: date.toISOString() });
                    }
                  }}
                  css={{
                    position: 'absolute',
                    right: '$8',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    opacity: 0,
                    cursor: 'pointer',
                    margin: 0,
                    padding: 0
                  }}
                />
                <Box
                  css={{
                    position: 'absolute',
                    right: '$10',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#4A9CA6',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <CalendarIcon size={20} />
                </Box>
              </Box>
            </Box>

            <Box css={{ flex: 1, minWidth: 220 }}>
              <Text
                weight="medium"
                size="sm"
                css={{ marginBottom: '$4', color: '#333333' }}
              >
                To
              </Text>
              <Box css={{ position: 'relative' }}>
                <Input
                  type="text"
                  placeholder="2026-07-08T00:00:00.000Z"
                  value={dateRange.to}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, to: e.currentTarget.value })
                  }
                  css={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '$sm',
                    padding: '$8 $36 $8 $8',
                    backgroundColor: '#FFFFFF',
                    paddingRight: '$36'
                  }}
                />
                <Input
                  type="date"
                  onChange={(e) => {
                    if (e.currentTarget.value) {
                      const date = new Date(
                        e.currentTarget.value + 'T00:00:00.000Z'
                      );
                      setDateRange({ ...dateRange, to: date.toISOString() });
                    }
                  }}
                  css={{
                    position: 'absolute',
                    right: '$8',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    opacity: 0,
                    cursor: 'pointer',
                    margin: 0,
                    padding: 0
                  }}
                />
                <Box
                  css={{
                    position: 'absolute',
                    right: '$10',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#4A9CA6',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <CalendarIcon size={20} />
                </Box>
              </Box>
            </Box>

            <Box css={{ flex: 1, minWidth: 150 }}>
              <Text
                weight="medium"
                size="sm"
                css={{ marginBottom: '$4', color: '#333333' }}
              >
                App ID
              </Text>
              <Input
                type="text"
                placeholder="Ex: 11430"
                value={appIdInput}
                onChange={(e) => setAppIdInput(e.currentTarget.value)}
                css={{ width: '100%', padding: '$8' }}
              />
            </Box>

            <Box css={{ flex: 1, minWidth: 200 }}>
              <Text
                weight="medium"
                size="sm"
                css={{ marginBottom: '$4', color: '#333333' }}
              >
                Event Type
              </Text>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="lg"
                    color="default"
                    variant="outline"
                    rightIcon={<ChevronDownIcon />}
                    css={{
                      width: '100%',
                      justifyContent: 'space-between',
                      fontWeight: 400,
                      color: selectedEventId ? '#333333' : '#999999'
                    }}
                  >
                    {eventTypeOptions.find(
                      (event) => String(event.id) === selectedEventId
                    )?.name || 'All Events'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  sideOffset={4}
                  css={{
                    minWidth: 220,
                    maxHeight: 300,
                    overflowY: 'auto',
                    zIndex: 1000
                  }}
                >
                  <DropdownMenuItem
                    css={{ cursor: 'pointer' }}
                    onClick={() => setSelectedEventId('')}
                  >
                    All Events
                  </DropdownMenuItem>
                  {eventTypeOptions.map((event) => (
                    <DropdownMenuItem
                      key={event.id}
                      css={{ cursor: 'pointer' }}
                      onClick={() => setSelectedEventId(String(event.id))}
                    >
                      {event.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </Box>

            <Flex gap="$6">
              <Button
                size="lg"
                onClick={handleApplyFilters}
                css={{
                  minWidth: 90,
                  backgroundColor: '#4A9CA6',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#3A8A95'
                  }
                }}
              >
                Apply
              </Button>
              {hasActiveFilters && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleClearFilters}
                  css={{
                    minWidth: 80
                  }}
                >
                  Clear
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>
      ) : (
        /* Trace ID Search */
        <Box
          css={{
            backgroundColor: '#FAFAFA',
            borderRadius: '$lg',
            padding: '$12',
            marginBottom: '$20',
            border: '1px solid #E8E8E8'
          }}
        >
          <Flex gap="$12" alignItems="flex-end">
            <Box css={{ flex: 1 }}>
              <Text
                weight="medium"
                size="sm"
                css={{ marginBottom: '$4', color: '#333333' }}
              >
                Trace ID
              </Text>
              <Input
                type="text"
                placeholder="Enter Trace ID"
                value={traceIdInput}
                onChange={(e) => setTraceIdInput(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTraceIdSearch();
                }}
                css={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '$sm',
                  padding: '$8',
                  backgroundColor: '#FFFFFF'
                }}
              />
            </Box>
            <Flex gap="$6">
              <Button
                size="lg"
                onClick={handleTraceIdSearch}
                css={{
                  minWidth: 90,
                  backgroundColor: '#4A9CA6',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#3A8A95'
                  }
                }}
              >
                Search
              </Button>
              {traceIdInput && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleClearTraceIdSearch}
                  css={{
                    minWidth: 80
                  }}
                >
                  Clear
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Loading State */}
      {loading && <EventLogsTableSkeleton columnWidths={columnWidths} />}

      {/* Events Table */}
      {!loading && eventTraces.length > 0 && (
        <Box
          css={{
            borderRadius: '$lg',
            border: '1px solid #E8E8E8',
            overflow: 'hidden',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Box css={{ overflowX: 'auto' }}>
            <Table css={{ width: '100%', tableLayout: 'fixed' }}>
              <Thead>
                <Tr css={{ backgroundColor: '#F5F5F5' }}>
                  <Th
                    css={{
                      padding: 0,
                      textAlign: 'left',
                      width: columnWidths.traceId,
                      position: 'relative',
                      userSelect: 'none',
                      overflow: 'visible'
                    }}
                  >
                    <Box css={{ padding: '$12', position: 'relative' }}>
                      <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                        Trace ID
                      </Text>
                      <ResizeHandle
                        onMouseDown={handleMouseDown('traceId')}
                        isActive={resizingColumn === 'traceId'}
                      />
                    </Box>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'left',
                      width: columnWidths.eventType
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Event Type
                    </Text>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'center',
                      width: columnWidths.totalApps
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Total Apps
                    </Text>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'center',
                      width: columnWidths.success
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Success
                    </Text>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'center',
                      width: columnWidths.failed
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Failed
                    </Text>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'left',
                      width: columnWidths.startedAt
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Started At
                    </Text>
                  </Th>
                  <Th
                    css={{
                      padding: '$12',
                      textAlign: 'center',
                      width: columnWidths.status
                    }}
                  >
                    <Text weight="bold" size="sm" css={{ color: '#333333' }}>
                      Status
                    </Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {eventTraces.map((trace) => (
                  <Tr
                    key={trace.traceId}
                    css={{
                      borderBottom: '1px solid #E8E8E8',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#F9F9F9'
                      }
                    }}
                    onClick={() => {
                      setSelectedTrace(trace);
                      setShowDetails(true);
                    }}
                  >
                    <Td
                      css={{ width: columnWidths.traceId, overflow: 'hidden' }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <TruncatedTraceId
                          traceId={trace.traceId}
                          maxWidth={columnWidths.traceId - 24}
                        />
                      </Box>
                    </Td>
                    <Td
                      css={{
                        width: columnWidths.eventType,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <Text
                          size="sm"
                          weight="medium"
                          css={{
                            color: '#333333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}
                          title={trace.eventType}
                        >
                          {trace.eventType}
                        </Text>
                      </Box>
                    </Td>
                    <Td
                      css={{
                        textAlign: 'center',
                        width: columnWidths.totalApps,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <Text
                          size="sm"
                          weight="medium"
                          css={{ color: '#333333' }}
                        >
                          {trace.totalCount}
                        </Text>
                      </Box>
                    </Td>
                    <Td
                      css={{
                        textAlign: 'center',
                        width: columnWidths.success,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <StatusBadge
                          status={String(trace.successCount)}
                          variant="success"
                        />
                      </Box>
                    </Td>
                    <Td
                      css={{
                        textAlign: 'center',
                        width: columnWidths.failed,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <StatusBadge
                          status={String(trace.failedCount)}
                          variant={trace.failedCount > 0 ? 'error' : 'default'}
                        />
                      </Box>
                    </Td>
                    <Td
                      css={{
                        width: columnWidths.startedAt,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <Text
                          size="sm"
                          css={{
                            color: '#666666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}
                          title={formatDate(trace.startedAt)}
                        >
                          {formatDate(trace.startedAt)}
                        </Text>
                      </Box>
                    </Td>
                    <Td
                      css={{
                        textAlign: 'center',
                        width: columnWidths.status,
                        overflow: 'hidden'
                      }}
                    >
                      <Box css={{ padding: '$12' }}>
                        <StatusBadge
                          status={getStatusLabel(
                            trace.isFailed,
                            trace.isCompleted
                          )}
                          variant={
                            getStatusColor(
                              trace.isFailed,
                              trace.isCompleted
                            ) === '$positive600'
                              ? 'success'
                              : getStatusColor(
                                    trace.isFailed,
                                    trace.isCompleted
                                  ) === '$negative600'
                                ? 'error'
                                : 'default'
                          }
                        />
                      </Box>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            css={{
              padding: '$12 $16',
              borderTop: '1px solid #E8E8E8',
              backgroundColor: '#FAFAFA'
            }}
          >
            <Flex alignItems="center" gap="$8">
              <Text size="sm" css={{ color: '#666666' }}>
                Showing{' '}
                {eventTraces.length > 0
                  ? (currentPage - 1) *
                      (filters.pageSize || DEFAULT_PAGE_SIZE) +
                    1
                  : 0}{' '}
                -{' '}
                {Math.min(
                  currentPage * (filters.pageSize || DEFAULT_PAGE_SIZE),
                  totalCount
                )}{' '}
                of {totalCount}
              </Text>
            </Flex>

            <Flex gap="$4" alignItems="center">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  handlePageChange(Math.max(0, (filters.pageNumber ?? 0) - 1))
                }
                disabled={currentPage === 1}
                css={{
                  minWidth: 80
                }}
              >
                Previous
              </Button>

              <Flex gap="$2">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    const maxPagesToShow = 5;
                    if (totalPages <= maxPagesToShow) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, array) => {
                    if (index > 0 && array[index - 1] !== page - 1) {
                      return (
                        <Box key={`ellipsis-${page}`} css={{ px: '$2' }}>
                          <Text size="sm" css={{ color: '#999999' }}>
                            ...
                          </Text>
                        </Box>
                      );
                    }
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'solid' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(page - 1)}
                        css={{
                          minWidth: 40,
                          backgroundColor:
                            currentPage === page ? '#4A9CA6' : 'transparent',
                          color: currentPage === page ? 'white' : '#666666',
                          border:
                            currentPage === page ? 'none' : '1px solid #E0E0E0'
                        }}
                      >
                        {page}
                      </Button>
                    );
                  })}
              </Flex>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange((filters.pageNumber ?? 0) + 1)}
                disabled={currentPage >= totalPages}
                css={{
                  minWidth: 80
                }}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Empty State */}
      {!loading && eventTraces.length === 0 && (
        <Flex
          justifyContent="center"
          alignItems="center"
          css={{
            minHeight: 300,
            backgroundColor: '#F5F5F5',
            borderRadius: '$lg',
            flexDirection: 'column',
            gap: '$12',
            border: '1px solid #E8E8E8'
          }}
        >
          <Text size="lg" weight="medium" css={{ color: '#666666' }}>
            {searchMode === 'traceId' && !filters.traceId
              ? 'Search for a Trace ID'
              : 'No event logs found'}
          </Text>
          <Text size="sm" css={{ color: '#999999' }}>
            {searchMode === 'traceId' && !filters.traceId
              ? 'Enter a Trace ID above and click Search to view its execution details'
              : 'Try adjusting your filters or check back later'}
          </Text>
        </Flex>
      )}

      {/* Execution Details Modal */}
      {selectedTrace && (
        <ExecutionDetailsModal
          trace={selectedTrace}
          isOpen={showDetails}
          onClose={() => {
            setShowDetails(false);
            setSelectedTrace(null);
          }}
        />
      )}
    </Box>
  );
};

export default EventLogsList;
